import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import LandingLayout from './components/LandingLayout';
import { BRITANNICA_PUBLISHER, BritannicaLesson } from './data/britannica-publisher';
import PressoLessonPreview from './PressoLessonPreview';
import { CurriculumLesson } from './data/peninsula-district';

const SHADOW = '4px 4px 0px #1e1e1e';
const SHADOW_SM = '2px 2px 0px #1e1e1e';
const PRESSTO_PURPLE = '#5C4FB8';
const BRITANNICA_NAVY = '#003D7A';
const BRITANNICA_RED = '#E31D23';
const CREAM = '#F2EEE5';

const PLAN_LABELS: Record<string, string> = {
  'op-ed': 'Opinion', 'sequence': 'Sequence', 'cause-effect': 'Cause & Effect',
  'compare-contrast': 'Compare & Contrast', 'retell-the-narrative': 'Retell',
  'personal-narrative': 'Personal Narrative', 'description': 'Description',
  'problem-solution': 'Problem & Solution', 'basic': 'Basic',
};

const AREA_ICONS: Record<string, string> = {
  'Science': '🔬',
  'History': '📜',
  'Civics': '🏛️',
  'Social Studies': '🌍',
  'ELA': '✍️',
};

const BRITANNICA_VALUE_PROPS = [
  {
    icon: '📚',
    title: 'Research + writing, unified',
    body: 'Students read trusted Britannica content to build knowledge, then move directly into Pressto to write about it — no context-switching, no blank page.',
  },
  {
    icon: '🧩',
    title: 'Matched writing plans',
    body: 'Every Pressto activity pairs with a specific Britannica lesson and uses the writing structure that fits the content — cause & effect for history, description for science, opinion for civics.',
  },
  {
    icon: '✨',
    title: 'AI Writing Buddy',
    body: 'Helps students find their own words and keep going — it asks questions and prompts thinking, but never writes for them.',
  },
  {
    icon: '📰',
    title: 'Publish their work',
    body: 'Students can publish finished pieces to a class or school newspaper — giving Britannica research a real audience and a real purpose.',
  },
  {
    icon: '🔍',
    title: 'Behavioral AI detection',
    body: 'Pressto detects AI-generated writing through behavioral signals — typing patterns, pacing, revision history — not just the text itself. Teachers see what actually happened, not just what was submitted.',
  },
];

function CTAButton({ children, onClick, dark = true }: { children: React.ReactNode; onClick: () => void; dark?: boolean }) {
  return (
    <MuiButton
      variant="contained"
      onClick={onClick}
      sx={{
        backgroundColor: dark ? '#2C2C2C' : '#FFE082',
        color: dark ? '#fff' : '#2C2C2C',
        fontFamily: 'Montserrat, Inter, sans-serif', fontWeight: 700, fontSize: '1rem',
        textTransform: 'none', borderRadius: '6px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW, px: 4, py: 1.5,
        '&:hover': { boxShadow: SHADOW_SM, transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      {children}
    </MuiButton>
  );
}

function Chip({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <Box sx={{
      backgroundColor: bg, color, borderRadius: '4px', px: 0.75, py: 0.3,
      fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.6rem',
      textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap',
    }}>{label}</Box>
  );
}

function LessonCard({ lesson, onClick }: { lesson: BritannicaLesson; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        borderRadius: '8px', border: '2px solid #1e1e1e',
        boxShadow: SHADOW_SM, backgroundColor: '#fff', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': { boxShadow: '2px 2px 0px #1e1e1e', transform: 'translate(2px,2px)' },
        transition: 'all 100ms',
      }}
    >
      <Box sx={{ height: 6, backgroundColor: BRITANNICA_NAVY }} />
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
          <Chip label={lesson.grade} bg={BRITANNICA_NAVY} color="#fff" />
          {PLAN_LABELS[lesson.plan] && (
            <Chip label={PLAN_LABELS[lesson.plan]} bg={BRITANNICA_RED} color="#fff" />
          )}
        </Box>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '0.85rem', color: '#1e1e1e', mb: 0.5, lineHeight: 1.2 }}>
          {lesson.title}
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.72rem', color: '#888', mb: 0.75, fontStyle: 'italic' }}>
          Pairs with: {lesson.britannicaTitle}
        </Typography>
        <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#666', lineHeight: 1.5, flex: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {lesson.prompt}
        </Typography>
        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', color: BRITANNICA_NAVY, mt: 1.25, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Preview →
        </Typography>
      </Box>
    </Box>
  );
}

export default function BritannicaPublisherPage() {
  const navigate = useNavigate();
  const publisher = BRITANNICA_PUBLISHER;

  const [preview, setPreview] = useState<BritannicaLesson | null>(null);

  const byArea = useMemo(() => {
    const map: Record<string, BritannicaLesson[]> = {};
    publisher.lessons.forEach(l => {
      (map[l.area] = map[l.area] || []).push(l);
    });
    return map;
  }, [publisher]);

  return (
    <LandingLayout>
      {/* Hero */}
      <Box sx={{ backgroundColor: CREAM, borderBottom: '2px solid #1e1e1e', py: { xs: 8, md: 12 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 880, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', border: '2px solid #1e1e1e', borderRadius: '6px', px: 2, py: 1, mb: 4, boxShadow: SHADOW_SM }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: BRITANNICA_NAVY }} />
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Publisher Partnership · Grades {publisher.gradeRange}
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.2rem' }, lineHeight: 1.1, color: '#2C2C2C', mb: 2 }}>
            <Box component="span" sx={{ color: PRESSTO_PURPLE }}>Pressto</Box>
            {' + '}
            <Box component="span" sx={{ color: BRITANNICA_NAVY }}>Britannica</Box>
            <Box component="span" sx={{ color: BRITANNICA_RED }}>.</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.3rem' }, color: '#444', lineHeight: 1.6, mb: 2, fontWeight: 600 }}>
            Britannica gives students the knowledge.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: { xs: '1.1rem', md: '1.3rem' }, color: PRESSTO_PURPLE, lineHeight: 1.6, mb: 4, fontWeight: 700 }}>
            Pressto is the writing layer for Britannica.
          </Typography>

          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666', lineHeight: 1.6, mb: 5, maxWidth: 640, mx: 'auto' }}>
            {publisher.lessonCount} ready-to-use writing activities paired to real Britannica lessons — across science, history, civics, and social studies for grades {publisher.gradeRange}.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton onClick={() => navigate('/contact')}>Let&apos;s partner →</CTAButton>
            <CTAButton dark={false} onClick={() => navigate('/register')}>Try Pressto free</CTAButton>
          </Box>
        </Box>
      </Box>

      {/* Value props */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.6rem', color: '#1e1e1e', mb: 1.5, textAlign: 'center' }}>
            The knowledge platform needs a writing workspace
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1rem', color: '#666', lineHeight: 1.6, mb: 5, textAlign: 'center', maxWidth: 700, mx: 'auto' }}>
            Britannica is where students go to understand the world. Pressto is where they put that understanding into words — with structured plans, scaffolded support, and a real audience for their writing.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            {BRITANNICA_VALUE_PROPS.map(f => (
              <Box key={f.title} sx={{ border: '2px solid #1e1e1e', borderRadius: '8px', boxShadow: SHADOW_SM, p: 3, display: 'flex', gap: 2 }}>
                <Box component="span" sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{f.icon}</Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#1e1e1e', mb: 0.5 }}>{f.title}</Typography>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>{f.body}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Stats bar */}
      <Box sx={{ backgroundColor: '#E8EEF5', borderTop: '2px solid #1e1e1e', borderBottom: '2px solid #1e1e1e', py: 4, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', justifyContent: 'center', gap: { xs: 4, md: 8 }, flexWrap: 'wrap' }}>
          {[
            { n: publisher.lessonCount, label: 'paired writing activities' },
            { n: publisher.areaCount, label: 'subject areas' },
            { n: publisher.gradeRange, label: 'grade range', isText: true },
          ].map(s => (
            <Box key={s.label} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '2.4rem', color: BRITANNICA_NAVY, lineHeight: 1 }}>
                {s.isText ? s.n : `${s.n}+`}
              </Typography>
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#666', mt: 0.5 }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Lesson cards by area */}
      <Box sx={{ backgroundColor: '#F7F8FA', py: { xs: 6, md: 9 }, px: { xs: 3, md: 6 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#1e1e1e', mb: 1, textAlign: 'center' }}>
            One writing activity per Britannica lesson
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#888', mb: 5, textAlign: 'center' }}>
            Each card pairs with a real Britannica Learning lesson — students research in Britannica, then write in Pressto.
          </Typography>
          {publisher.areas.filter(area => byArea[area]?.length).map(area => (
            <Box key={area} sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: BRITANNICA_RED, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#1e1e1e' }}>
                  {AREA_ICONS[area]} {area}
                </Typography>
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.8rem', color: '#aaa', ml: 0.5 }}>
                  {byArea[area].length} {byArea[area].length === 1 ? 'activity' : 'activities'}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                {byArea[area].map((lesson, i) => (
                  <LessonCard key={i} lesson={lesson} onClick={() => setPreview(lesson)} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom CTA */}
      <Box sx={{ backgroundColor: BRITANNICA_NAVY, borderTop: '2px solid #1e1e1e', py: { xs: 8, md: 10 }, px: 3, textAlign: 'center' }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Typography variant="h2" sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800, fontSize: { xs: '1.7rem', md: '2.4rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Let&apos;s build the complete<br />research-to-writing experience together.
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '1.05rem', color: 'rgba(255,255,255,0.7)', mb: 5 }}>
            Britannica&apos;s trusted knowledge + Pressto&apos;s writing workspace = a K-8 program where every student researches, writes, and publishes.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <CTAButton dark={false} onClick={() => navigate('/contact')}>Get in touch →</CTAButton>
            <MuiButton
              variant="outlined"
              onClick={() => navigate('/register')}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.4)',
                fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1rem',
                textTransform: 'none', borderRadius: '6px', px: 4, py: 1.5,
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Try Pressto free
            </MuiButton>
          </Box>
        </Box>
      </Box>

      {preview && (
        <PressoLessonPreview
          lesson={preview as unknown as CurriculumLesson}
          onClose={() => setPreview(null)}
          onRegister={() => navigate('/register')}
          onPricing={() => navigate('/contact')}
        />
      )}
    </LandingLayout>
  );
}
